import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSEO, buildMenuLD, RESTAURANT_LD } from "@/lib/seo";
import { Leaf, Star, Flame } from "lucide-react";
import { useI18n } from "@/i18n";

export default function MenuPage() {
  const [cats, setCats] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, lang, alternateUrls } = useI18n();

  useSEO({
    title: t("menu.title"),
    description: lang === "en"
      ? "Discover L'Entracte's menu in Ville d'Avray: starters, mains, desserts and wines. Entirely house-made French cuisine from fresh, seasonal produce."
      : "Découvrez la carte de L'Entracte à Ville d'Avray : entrées, plats, desserts et vins. Cuisine française entièrement faite maison à base de produits frais et de saison.",
    keywords: lang === "en"
      ? "L'Entracte menu, French cuisine Ville d'Avray, signature dishes, fine dining"
      : "carte restaurant Ville d'Avray, menu gastronomique, cuisine française, plats signature",
    lang,
    alternates: alternateUrls(),
    jsonLd: [buildMenuLD(cats, dishes), RESTAURANT_LD],
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([api.get("/menu/categories"), api.get("/menu/dishes")]);
        setCats(c.data);
        setDishes(d.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="menu-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-20 md:py-28 max-w-[1500px] mx-auto">
        <p className="overline">{t("menu.overline")}</p>
        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">{t("menu.title")}</h1>
        <p className="mt-6 text-[#697585] text-base md:text-lg max-w-2xl">
          {t("menu.sub")}
        </p>

        <div className="mt-10 inline-flex flex-wrap items-center gap-3 border-l-2 border-[#B8956A] pl-5 py-2 bg-white">
          <Star size={14} className="text-[#B8956A] fill-[#B8956A]" />
          <p className="text-sm text-[#203240]">
            {t("menu.lunchBanner")}
          </p>
        </div>
      </section>

      {loading && <p className="text-[#697585] pb-32">{t("menu.loading")}</p>}

      <div className="max-w-[1500px] mx-auto pb-24 md:pb-32 space-y-24 md:space-y-28">
        {cats.map((cat) => {
          const items = dishes.filter((d) => d.category_id === cat.id && d.available);
          if (items.length === 0) return null;
          // Optional EN translation fallback (if backend later provides name_en / description_en)
          const catName = lang === "en" ? (cat.name_en || cat.name) : cat.name;
          const catDesc = lang === "en" ? (cat.description_en || cat.description) : cat.description;
          return (
            <section key={cat.id} data-testid={`menu-cat-${cat.slug}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
                <div className="md:col-span-4">
                  <p className="overline">{`0${cat.order || 1}`.slice(-2)} — {t("menu.section")}</p>
                  <h2 className="font-display text-4xl md:text-5xl mt-4">{catName}</h2>
                  {catDesc && (
                    <p className="mt-4 text-[#697585]">{catDesc}</p>
                  )}
                </div>
                <div className="md:col-span-8 space-y-6">
                  {items.map((d) => {
                    const dName = lang === "en" ? (d.name_en || d.name) : d.name;
                    const dDesc = lang === "en" ? (d.description_en || d.description) : d.description;
                    return (
                      <div key={d.id} data-testid={`dish-${d.id}`} className="pb-6 border-b border-dashed border-[#D9D3C5]">
                        <div className="menu-leader">
                          <h3 className="font-display text-xl md:text-2xl text-[#203240] flex items-center gap-2">
                            {dName}
                            {d.is_signature && <Star size={14} className="text-[#B8956A] fill-[#B8956A]" />}
                            {d.is_vegetarian && <Leaf size={14} className="text-emerald-600" />}
                            {d.is_spicy && <Flame size={14} className="text-red-500" />}
                          </h3>
                          <span className="dots" />
                          <span className="font-display text-lg md:text-xl text-[#B8956A]">{d.price.toFixed(2)} €</span>
                        </div>
                        {dDesc && (
                          <p className="mt-2 text-[#697585] text-sm md:text-base">{dDesc}</p>
                        )}
                        {d.allergens && (
                          <p className="mt-2 text-xs uppercase tracking-widest text-[#697585]/70">
                            {t("menu.allergens")} : {d.allergens}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <p className="text-center text-xs text-[#697585] pb-16">
        <Star size={12} className="inline text-[#B8956A] fill-[#B8956A] mr-1" /> {t("menu.legend.star")} ·
        <Leaf size={12} className="inline text-emerald-400 mx-2" /> {t("menu.legend.veg")} ·
        <Flame size={12} className="inline text-red-500 mx-2" /> {t("menu.legend.spicy")}
      </p>
    </div>
  );
}
